import React from 'react'
import { Dialog, IconButton } from '@mui/material'
import { AlertCircle, X } from 'lucide-react'
import Button from '../../ui/Button'
import type { PendingColumnTypeChange } from '../../../hooks/useAppendixTableDraft'
import {
  describeInvalidValues,
  formatCount,
  formatValueExamples,
  getColumnTypeNoun,
} from '../../../utils/appendixTable'

type ColumnTypeChangeDialogProps = {
  change: PendingColumnTypeChange | null
  onKeep: () => void
  onConfirm: () => void
}

const TYPE_ADJECTIVES: Record<string, string> = {
  Number: 'numeric',
  Boolean: 'boolean',
}

function describeChange(change: PendingColumnTypeChange) {
  const isCodeItemSwap = change.fromType === 'CodeItem' && change.toType === 'CodeItem'
  if (isCodeItemSwap) {
    return {
      title: `Point ${change.columnLabel} to code item ${change.toCodeItemCode}?`,
      keepLabel: change.fromCodeItemCode ? `Keep ${change.fromCodeItemCode}` : 'Keep current',
      confirmLabel: `Change to ${change.toCodeItemCode}`,
    }
  }

  const adjective = TYPE_ADJECTIVES[change.toType] || getColumnTypeNoun(change.toType)
  return {
    title: `Change ${change.columnLabel} to a ${adjective} column?`,
    keepLabel: `Keep as ${getColumnTypeNoun(change.fromType)}`,
    confirmLabel: `Change to ${getColumnTypeNoun(change.toType)}`,
  }
}

export const ColumnTypeChangeDialog: React.FC<ColumnTypeChangeDialogProps> = ({ change, onKeep, onConfirm }) => {
  if (!change) return null

  const count = change.clearedValues.length
  const { title, keepLabel, confirmLabel } = describeChange(change)
  const examples = formatValueExamples(change.clearedValues)
  const holds = count === 1 ? 'holds a value that isn\'t' : 'hold values that aren\'t'
  const outcome = count === 1 ? 'That cell will be cleared.' : 'Those cells will be cleared.'

  return (
    <Dialog
      open
      onClose={onKeep}
      maxWidth="xs"
      fullWidth
      aria-labelledby="column-type-change-title"
      PaperProps={{ sx: { borderRadius: '8px' } }}
    >
      <div className="flex items-start gap-3 border-b border-amber-200 bg-amber-50 px-5 py-4">
        <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />
        <h2 id="column-type-change-title" className="min-w-0 flex-1 text-base font-semibold text-gray-900">
          {title}
        </h2>
        <IconButton size="small" onClick={onKeep} aria-label="Close">
          <X className="h-4 w-4 text-[#1D5176]" aria-hidden="true" />
        </IconButton>
      </div>
      <div className="px-5 py-4 text-sm text-gray-700">
        {`${formatCount(count, 'row')} ${holds} ${describeInvalidValues(change.toType, change.toCodeItemCode, count)}${examples ? `, such as ${examples}` : ''}. ${outcome}`}
      </div>
      <div className="flex justify-end gap-2 px-5 pb-4">
        <Button type="button" variant="secondary" size="sm" className="bg-[#EEF6FF]" onClick={onKeep}>
          {keepLabel}
        </Button>
        <button
          type="button"
          className="inline-flex cursor-pointer items-center justify-center rounded bg-[#D11A1A] px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[#B01515] focus:outline-none focus:ring-2 focus:ring-offset-1"
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
      </div>
    </Dialog>
  )
}

export default ColumnTypeChangeDialog
